import React from 'react';
import { Info } from '@/utils/icons';

interface InfoTooltipProps {
  /** Tooltip title text */
  title: string;
  /** Tooltip body text */
  body: string;
  /** Dark mode flag for styling */
  isDark: boolean;
  /** Tailwind width class (e.g., 'w-72', 'w-80'). Defaults to 'w-72' */
  width?: string;
  /** Icon size in Tailwind units (3, 3.5, 4). Defaults to 3.5 */
  iconSize?: number;
  /** Optional aria-label override. Defaults to "More information" */
  ariaLabel?: string;
  /** Horizontal alignment of the tooltip card. 'left' anchors to left edge, 'right' anchors to right edge. Defaults to 'left' */
  align?: 'left' | 'right';
}

/**
 * Reusable info tooltip component with hover-triggered display.
 * Renders an info icon that shows a styled tooltip card on hover.
 *
 * Usage:
 * ```tsx
 * <InfoTooltip
 *   title="About HRV"
 *   body="Heart rate variability compared to your baseline."
 *   isDark={isDark}
 * />
 * ```
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  title,
  body,
  isDark,
  width = 'w-72',
  iconSize = 3.5,
  ariaLabel = 'More information',
  align = 'left',
}) => {
  // Convert numeric icon size to Tailwind classes
  const iconSizeClass = iconSize === 3 ? 'w-3 h-3' : iconSize === 4 ? 'w-4 h-4' : 'w-3.5 h-3.5';

  return (
    <span
      className="group/tooltip relative inline-flex text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help shrink-0"
      aria-label={ariaLabel}
    >
      <Info className={iconSizeClass} />
      {/* Tooltip card - visible on hover */}
      <span
        className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full z-[9999] mt-1.5 ${width} p-3 rounded-xl border shadow-lg text-left opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-150 pointer-events-none group-hover/tooltip:pointer-events-auto ${
          isDark
            ? 'bg-neutral-900 border-neutral-600 text-neutral-200'
            : 'bg-white border-gray-200 text-gray-900'
        }`}
        role="tooltip"
      >
        <div
          className="text-xs font-semibold mb-1.5"
          style={{ color: isDark ? '#F9FAFB' : '#111827' }}
        >
          {title}
        </div>
        <p
          className="text-xs leading-relaxed"
          style={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
        >
          {body}
        </p>
      </span>
    </span>
  );
};

export default InfoTooltip;
